import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import PageMeta from "@/components/feature/PageMeta";
import ArticleCard from "@/components/feature/ArticleCard";
import {
  categories,
  getArticlesByCategory,
  getCategoryBySlug,
} from "@/mocks/data";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const activeCategory = getCategoryBySlug(activeSlug);
  const categoryArticles = getArticlesByCategory(activeSlug);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: activeCategory ? `${activeCategory.name} - 免费91网页` : "内容分类",
    description: activeCategory?.description || "",
    url: `https://www.91web.com/category/${activeSlug}`,
  };

  return (
    <>
      <PageMeta
        title={
          activeCategory
            ? `${activeCategory.name} - 免费91网页分类页面`
            : "内容分类 - 免费91网页"
        }
        description={
          activeCategory?.description ||
          "免费91网页为您提供丰富多样的内容分类，涵盖科技数码、生活百科、娱乐休闲、教育学习、健康养生等领域，免费畅读。"
        }
        keywords={`免费91网页,${activeCategory?.name || ""},91网页免费入口,免费91网页在线访问,免费91网页最新地址`}
        canonicalUrl={`https://www.91web.com/category/${activeSlug}`}
      />
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollToTop />

        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: activeCategory?.name || "分类" },
          ]}
        />

        <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          {/* Category Header */}
          <section className="mb-10">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              {activeCategory?.name}
            </h1>
            <p className="text-foreground-600 max-w-2xl">
              {activeCategory?.description}
            </p>
          </section>

          {/* Category Tabs */}
          <div className="mb-8 overflow-x-auto -mx-4 px-4">
            <div className="flex gap-2 min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  onClick={() => setActiveSlug(cat.slug)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    activeSlug === cat.slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200/70"
                  }`}
                >
                  <i className={`${cat.icon} mr-1.5`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Articles Grid */}
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categoryArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
                <i className="ri-file-search-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">暂无相关内容</p>
            </div>
          )}

          {/* Pagination placeholder */}
          <div className="flex items-center justify-center gap-2 mt-12">
            <button className="w-9 h-9 flex items-center justify-center rounded-md text-sm text-foreground-400 cursor-not-allowed">
              <i className="ri-arrow-left-s-line"></i>
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-md text-sm bg-primary-500 text-background-50 font-medium">
              1
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-md text-sm text-foreground-600 hover:bg-background-100">
              2
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-md text-sm text-foreground-600 hover:bg-background-100">
              3
            </button>
            <span className="text-foreground-400 mx-1">...</span>
            <button className="w-9 h-9 flex items-center justify-center rounded-md text-sm text-foreground-600 hover:bg-background-100">
              8
            </button>
            <button className="w-9 h-9 flex items-center justify-center rounded-md text-sm text-foreground-600 hover:bg-background-100">
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}